import React, { useState, Fragment, useEffect } from "react";
import { PMDialog } from "../../../utils/PMDialog";
import { PMButton } from "../../../utils/PMButton";
import ChecklistItemLinesCompactV2 from "../../quality/checklists/checklistItemLinesCompactV2";
import { updateReportChecklist } from "../../../../api/service/report";
import { PMNotice } from "../../../utils/notifications";
import { ChecklistTypeSelector } from "../../quality/checklists/checklistTypeSelector";
import { createReportChecklist, fetchReportChecklist } from "../../../../api/service/report";
import toast from "react-hot-toast";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";


const ReportChecklistDialogComponent = ({
  open,
  onClose,
  report,
  onRefresh,
}) => {
  const [checklist, setChecklist] = useState(null);
  const [isLoadingChecklist, setIsLoadingChecklist] = useState(true);
  const [checklistTypeSelectorOpen, setChecklistTypeSelectorOpen] = useState(false);
  const [apiErrors, setApiErrors] = useState([]);

  // Determine if user can edit this report
  const canEdit =
    report?.user_can_edit_with_rights !== false ||
    report?.user_can_edit !== false;

  const loadChecklist = async () => {
    if (!report?.id) {
      setIsLoadingChecklist(false);
      setChecklist(null);
      return;
    }

    setIsLoadingChecklist(true);
    setApiErrors([]);
    try {
      const response = await fetchReportChecklist(report.id, {
        fields: "id,title,description,status,is_items_editable,items.*,checklist_type.id,checklist_type.displayTitle",
      });
      
      // Check if response has errors (from backend)
      if (response?.errors) {
        // If 404 error, it means no checklist exists yet - that's fine
        if (response.errors.some(err => err.includes("does not have a checklist") || err.includes("404"))) {
          setChecklist(null);
          setApiErrors([]);
        } else {
          setApiErrors(response.errors);
          setChecklist(null);
        }
        return;
      }
      
      // Response can be either response.data (from axios) or just data (from our wrapper)
      const checklistData = response?.data || response;
      if (checklistData && checklistData.id) {
        // Ensure items is always an array
        if (!checklistData.items || !Array.isArray(checklistData.items)) {
          checklistData.items = [];
        }
        setChecklist(checklistData);
      } else {
        // If no checklist exists, that's fine - user can create one
        setChecklist(null);
      }
    } catch (error) {
      console.error("Error loading checklist:", error);
      // If 404, it means no checklist exists yet - that's fine
      if (error?.response?.status === 404 || error?.status === 404) {
        setChecklist(null);
        setApiErrors([]);
      } else {
        const errorMessage = error?.response?.data?.errors?.join(", ") ||
                            error?.response?.data?.error ||
                            error?.message ||
                            "Kunne ikke laste sjekkliste";
        setApiErrors([errorMessage]);
        setChecklist(null);
      }
    } finally {
      setIsLoadingChecklist(false);
    }
  };

  // Load checklist when dialog opens
  useEffect(() => {
    if (open && report?.id) {
      loadChecklist();
    } else {
      setChecklist(null);
      setIsLoadingChecklist(true);
      setApiErrors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, report?.id]);

  const submitCreateChecklist = async (selectedChecklistType, checklistTitle) => {
    try {
      const response = await createReportChecklist({
        report: {
          id: report?.id,
          checklist_type_id: selectedChecklistType,
          ...(checklistTitle
            ? { checklist_title: checklistTitle }
            : {}),
        },
        fields: "id,checklist",
      });

      if (response.data.id && response.data.checklist) {
        toast.success("Sjekkliste opprettet");
        // Reload checklist (which will filter empty items)
        await loadChecklist();
        // Update report with new checklist data
        const updatedReport = {
          ...report,
          checklist: {
            id: response.data.checklist.id,
          },
        };
        onRefresh(updatedReport);
        setChecklistTypeSelectorOpen(false);
      }
    } catch (err) {
      toast.error("Kunne ikke opprette sjekkliste");
      console.error("Error creating checklist:", err);
    }
  };


  return (
    <Fragment>
      <PMDialog
        title="Sjekkliste"
        open={open}
        onClose={onClose}
        size="5xl"
        maxWidth="7xl"
      >
        <div className="space-y-6">
          {!canEdit && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Kun visning
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      Denne sjekklisten kan ikke redigeres. Du kan se innholdet,
                      men ikke lagre endringer.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="text-sm text-gray-600">
            Fullfør sjekkliste for rapporten
          </div>

          {/* API Errors (for save/update errors, not loading errors) */}
          {apiErrors.length > 0 && checklist && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Feil ved lagring
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <ul className="list-disc list-inside">
                      {apiErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Show loading state while checklist data is being loaded */}
          {isLoadingChecklist ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Laster sjekkliste...</span>
            </div>
          ) : apiErrors.length > 0 ? (
            <>
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Feil ved lasting av sjekkliste
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <ul className="list-disc list-inside">
                        {apiErrors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p>Kunne ikke laste sjekkliste. Vennligst prøv igjen senere eller kontakt support hvis problemet vedvarer.</p>
              </div>
            </>
          ) : !checklist && !isLoadingChecklist ? (
            <>
              <div className="py-5 px-5 rounded-md shadow-xl mb-20">
                <PMNotice
                  title="Sjekkliste mangler"
                  description="Rapporten er ikke knyttet til sjekkliste. Velg en sjekkliste type nedenfor for å opprette en."
                />

                <div className="mt-6">
                  <ChecklistTypeSelector
                    open={checklistTypeSelectorOpen}
                    onClose={() => setChecklistTypeSelectorOpen(false)}
                    report={report}
                    onChange={(checklistType, { title }) => {
                      submitCreateChecklist(checklistType.id, title);
                    }}
                  />
                  
                  <button
                    type="button"
                    onClick={() => setChecklistTypeSelectorOpen(true)}
                    disabled={!canEdit}
                    className="w-full inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                    Velg sjekkliste type
                  </button>
                </div>
              </div>
            </>
          ) : checklist ? (
            <div className="space-y-6">
              <ChecklistItemLinesCompactV2
                checklist={checklist}
                items={checklist?.items || []}
                reportId={report?.id}
                editable={canEdit && checklist?.is_items_editable}
                disabled={
                  !canEdit ||
                  (checklist?.is_items_editable !== undefined &&
                    !checklist?.is_items_editable)
                }
                validationErrors={{}}
                updateChecklistItems={async (id, payload) => {
                  return await updateReportChecklist(id, payload);
                }}
                onItemsUpdate={(updatedItems) => {
                  // Update checklist state when items are updated
                  const updatedChecklist = {
                    ...checklist,
                    items: updatedItems,
                  };
                  setChecklist(updatedChecklist);
                  
                  // Notify parent component about the update with full checklist data
                  // This allows parent to update checklist status without refetching
                  if (onRefresh && report) {
                    onRefresh({
                      ...report,
                      // Include minimal checklist data for status calculation
                      checklist: {
                        id: updatedChecklist.id,
                        items: updatedItems,
                      },
                    });
                  }
                }}
              />

              {/* Close button */}
              <div className="flex justify-end pt-6 border-t">
                <PMButton
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  className="bg-gray-500 hover:bg-gray-600 text-white border-gray-500 hover:border-gray-600"
                >
                  Lukk
                </PMButton>
              </div>
            </div>
          ) : null}
        </div>
      </PMDialog>

    </Fragment>
  );
};

// Export component directly without React.memo to ensure re-renders when report changes
export const ReportChecklistDialog = ReportChecklistDialogComponent;

